import API_BASE_URL, { authHeader } from './apiConfig';

/**
 * Product Service
 * Handles all API calls related to products
 */

/**
 * Send product fields as multipart/form-data together with an image file.
 * Uses XMLHttpRequest so the caller can show upload progress (0-100).
 * Null values are sent as '' (the API treats '' as "clear this field").
 */
const sendProductWithImage = (method, url, productData, imageFile, onProgress) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    Object.entries(productData).forEach(([key, value]) => {
      if (value === undefined) return;
      formData.append(key, value === null ? '' : String(value));
    });
    formData.append('image', imageFile);

    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.responseType = 'json';
    // Admin-only endpoint: XMLHttpRequest doesn't go through the fetch wrapper, so add the token here
    Object.entries(authHeader()).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
      };
    }
    xhr.onload = () => {
      const body = xhr.response || {};
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body);
      } else {
        reject(new Error(body.error || (xhr.status === 404 ? 'Product not found' : 'Failed to save product')));
      }
    };
    xhr.onerror = () => reject(new Error('Network error while uploading the image. Please try again.'));
    xhr.send(formData);
  });

/**
 * Create a new product
 * @param {Object} productData - Product data to create
 * @param {File} [imageFile] - Optional image, uploaded to Cloudinary by the API
 * @param {Function} [onProgress] - Called with upload progress (0-100) when an image is sent
 * @returns {Promise<Object>} Created product
 */
export const createProduct = async (productData, imageFile, onProgress) => {
  try {
    if (imageFile) {
      return await sendProductWithImage('POST', `${API_BASE_URL}/products`, productData, imageFile, onProgress);
    }
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create product');
    }

    const product = await response.json();
    return product;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

/**
 * Get all products
 * @returns {Promise<Array>} Array of products
 */
export const getAllProducts = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch products');
    }

    const products = await response.json();
    return products;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Get a single product by ID
 * @param {string|number} productId - Product ID
 * @returns {Promise<Object>} Product object
 */
export const getProductById = async (productId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Product not found');
      }
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch product');
    }

    const product = await response.json();
    return product;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

/**
 * Update a product
 * @param {string|number} productId - Product ID
 * @param {Object} productData - Updated product data
 * @param {File} [imageFile] - Optional new image; replaces the current one
 * @param {Function} [onProgress] - Called with upload progress (0-100) when an image is sent
 * @returns {Promise<Object>} Updated product
 */
export const updateProduct = async (productId, productData, imageFile, onProgress) => {
  try {
    if (imageFile) {
      return await sendProductWithImage('PUT', `${API_BASE_URL}/products/${productId}`, productData, imageFile, onProgress);
    }
    const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Product not found');
      }
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update product');
    }

    const product = await response.json();
    return product;
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

/**
 * Delete a product
 * @param {string|number} productId - Product ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteProduct = async (productId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Product not found');
      }
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete product');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

// Export all functions as default object for convenience
export default {
  createProduct,
  getAllProducts,
  getProductById,
  updateProduct,
  deleteProduct,
};

